import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, Trash2, Camera, Images, X } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, TextArea, Select, Button } from "@/components/ui-kit";
import { getMeta, listIngredients, getSheet, createSheet, updateSheet, uploadFile } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { SHEET_CATEGORIES } from "@/constants/records";

export default function SheetEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: meta } = useSWR("meta", getMeta);
  const { data: ingredients } = useSWR("ingredients", listIngredients);
  const { data: existing } = useSWR(id ? ["sheet", id] : null, () => getSheet(id));

  const [form, setForm] = useState({ name: "", legal_name: "", base: "", category: "", conservation: "", traces: "", elaboration_zone: "", equipment: "", responsibles: "" });
  const [rows, setRows] = useState([]); // { ingredient_id, quantity, provider }
  const [steps, setSteps] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name || "", legal_name: existing.legal_name || "",
        base: existing.base || "", category: existing.category || "", conservation: existing.conservation || "", traces: existing.traces || "",
        elaboration_zone: existing.elaboration_zone || "", equipment: existing.equipment || "", responsibles: existing.responsibles || "",
      });
      setRows((existing.ingredients || []).map((i) => ({ ingredient_id: i.ingredient_id, quantity: i.quantity || "", provider: i.provider || "" })));
      setSteps(existing.steps || []);
      setPhotos(existing.photos || []);
    }
  }, [existing]);

  const allergensMeta = meta?.allergens || [];
  const ingList = ingredients || [];
  const ingById = Object.fromEntries(ingList.map((i) => [i.id, i]));
  const derived = (() => {
    const s = new Set();
    rows.forEach((r) => ingById[r.ingredient_id]?.allergens?.forEach((a) => s.add(a)));
    return allergensMeta.filter((a) => s.has(a.id));
  })();

  const addRow = () => setRows((r) => [...r, { ingredient_id: "", quantity: "", provider: "" }]);
  const setRow = (idx, k, v) => setRows((r) => r.map((row, i) => (i === idx ? { ...row, [k]: v } : row)));
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const addStep = () => setSteps((s) => [...s, ""]);
  const setStep = (idx, v) => setSteps((s) => s.map((x, i) => (i === idx ? v : x)));
  const removeStep = (idx) => setSteps((s) => s.filter((_, i) => i !== idx));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const f = await resizeImage(file);
        const { url } = await uploadFile(f);
        setPhotos((p) => [...p, url]);
      }
    } catch { toast.error("No s'ha pogut pujar la foto"); }
    finally { setUploading(false); }
  };
  const removePhoto = (url) => setPhotos((p) => p.filter((u) => u !== url));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Posa un nom a la fitxa");
    const cleanRows = rows.filter((r) => r.ingredient_id).map((r) => ({ ingredient_id: r.ingredient_id, quantity: r.quantity.trim(), provider: r.provider.trim() }));
    const cleanSteps = steps.map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), legal_name: form.legal_name.trim(), base: form.base.trim(),
        category: form.category, conservation: form.conservation.trim(), traces: form.traces.trim(),
        elaboration_zone: form.elaboration_zone.trim(), equipment: form.equipment.trim(), responsibles: form.responsibles.trim(),
        ingredients: cleanRows, steps: cleanSteps, photos,
      };
      if (!id) payload.active = true; // en crear sí; en editar no toquem la visibilitat
      const saved = id ? await updateSheet(id, payload) : await createSheet(payload);
      toast.success("Fitxa desada");
      navigate(`/production/${saved.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error desant la fitxa"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Header title={id ? "Editar fitxa" : "Nova fitxa"} />
      <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 sm:px-6 sm:pt-10">
        <h2 className="animate-fade-up font-display text-2xl font-bold text-ink sm:text-3xl">{id ? "Editar fitxa de producció" : "Nova fitxa de producció"}</h2>
        <div className="mt-6 space-y-4">
          <Card>
            <div className="space-y-4">
              <div><div className="label-eyebrow mb-2">Nom del producte</div><TextInput testid="sheet-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex: Hamburguesa de pollastre natural" /></div>
              <div><div className="label-eyebrow mb-2">Denominació legal (opcional)</div><TextInput testid="sheet-legal" value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} placeholder="Ex: Hamburguesa d'aviram" /></div>
              <div><div className="label-eyebrow mb-2">Base (opcional)</div><TextInput testid="sheet-base" value={form.base} onChange={(e) => set("base", e.target.value)} placeholder="Ex: Picada natural de pollastre" /></div>
              <div><div className="label-eyebrow mb-2">Categoria</div>
                <Select testid="sheet-category" value={form.category} onChange={(e) => set("category", e.target.value)}>
                  <option value="">— Altres —</option>
                  {SHEET_CATEGORIES.filter((c) => c !== "Altres").map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
            </div>
          </Card>

          <Card>
            <div className="label-eyebrow mb-2">Foto del producte</div>
            <div className="flex flex-wrap gap-3">
              {photos.map((url) => (
                <div key={url} className="relative h-20 w-20">
                  <img src={url} alt="" className="h-20 w-20 rounded-xl border border-line object-cover" />
                  <button type="button" onClick={() => removePhoto(url)} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-ember text-white shadow-card"><X size={14} /></button>
                </div>
              ))}
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line bg-white text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                <Camera size={20} />
                <span className="text-[10px] font-semibold uppercase tracking-wide">{uploading ? "Pujant…" : "Càmera"}</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotos} disabled={uploading} />
              </label>
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line bg-white text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                <Images size={20} />
                <span className="text-[10px] font-semibold uppercase tracking-wide">Galeria</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} disabled={uploading} />
              </label>
            </div>
          </Card>

          <Card>
            <div className="mb-3 flex items-center justify-between">
              <div className="label-eyebrow">Ingredients</div>
              <Button variant="secondary" onClick={addRow} testid="sheet-add-ingredient"><Plus size={16} /> Afegir</Button>
            </div>
            {ingList.length === 0 && <div className="text-sm text-ink-soft">No hi ha ingredients al catàleg. Crea'n a <strong>Ingredients</strong> primer.</div>}
            <div className="space-y-3">
              {rows.map((row, idx) => (
                <div key={idx} className="rounded-xl border border-line p-3">
                  <div className="flex items-center gap-2">
                    <Select testid={`sheet-ing-${idx}`} value={row.ingredient_id} onChange={(e) => setRow(idx, "ingredient_id", e.target.value)} className="flex-1">
                      <option value="">— tria ingredient —</option>
                      {ingList.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </Select>
                    <button onClick={() => removeRow(idx)} className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-ink-soft hover:bg-ember-bg hover:text-ember" aria-label="Treure"><Trash2 size={18} /></button>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <TextInput testid={`sheet-qty-${idx}`} value={row.quantity} onChange={(e) => setRow(idx, "quantity", e.target.value)} placeholder="Quantitat (ex: ~90%)" className="flex-1" />
                    <TextInput testid={`sheet-prov-${idx}`} value={row.provider} onChange={(e) => setRow(idx, "provider", e.target.value)} placeholder="Proveïdor" className="flex-1" />
                  </div>
                </div>
              ))}
            </div>
            {derived.length > 0 && (
              <div className="mt-4 rounded-xl bg-ember-bg p-3">
                <div className="label-eyebrow mb-1.5 text-ember">Al·lèrgens (automàtic)</div>
                <div className="flex flex-wrap gap-1.5">{derived.map((a) => <span key={a.id} className="rounded-full bg-ember px-2 py-0.5 text-xs font-semibold text-white">{a.name}</span>)}</div>
              </div>
            )}
          </Card>

          <Card>
            <div><div className="label-eyebrow mb-2">Conservació (opcional)</div><TextArea testid="sheet-conservation" value={form.conservation} onChange={(e) => set("conservation", e.target.value)} placeholder="Ex: ≤ 2°C · Vida útil 3 dies" /></div>
            <div className="mt-4"><div className="label-eyebrow mb-2">Trazas (opcional)</div><TextArea testid="sheet-traces" value={form.traces} onChange={(e) => set("traces", e.target.value)} placeholder="Avís de trazas" /></div>
          </Card>

          <Card>
            <div className="label-eyebrow mb-3">Elaboració</div>
            <div className="space-y-4">
              <div><div className="label-eyebrow mb-2">Zona d'elaboració</div><TextInput testid="sheet-zone" value={form.elaboration_zone} onChange={(e) => set("elaboration_zone", e.target.value)} placeholder="Ex: Obrador" /></div>
              <div><div className="label-eyebrow mb-2">Maquinària / estris</div><TextInput testid="sheet-equipment" value={form.equipment} onChange={(e) => set("equipment", e.target.value)} placeholder="Ex: Picadora MAINCA · Embutidora Brunetti" /></div>
              <div><div className="label-eyebrow mb-2">Responsables</div><TextInput testid="sheet-responsibles" value={form.responsibles} onChange={(e) => set("responsibles", e.target.value)} placeholder="Ex: Marc, Hugo, Priya, Lucía" /></div>
            </div>
          </Card>

          <Card>
            <div className="mb-3 flex items-center justify-between">
              <div className="label-eyebrow">Passos d'elaboració</div>
              <Button variant="secondary" onClick={addStep} testid="sheet-add-step"><Plus size={16} /> Afegir</Button>
            </div>
            <div className="space-y-2">
              {steps.map((st, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="w-5 font-display text-sm font-bold text-ink-soft">{idx + 1}.</span>
                  <TextInput testid={`sheet-step-${idx}`} value={st} onChange={(e) => setStep(idx, e.target.value)} placeholder="Descriu el pas" className="flex-1" />
                  <button onClick={() => removeStep(idx)} className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-ink-soft hover:bg-ember-bg hover:text-ember" aria-label="Treure"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-bg/95 backdrop-blur-md">
        <div className="mx-auto max-w-2xl px-4 py-3 sm:px-6">
          <Button onClick={save} disabled={saving || uploading} testid="sheet-save" className="w-full">{saving ? "Desant…" : "Desar fitxa"}</Button>
        </div>
      </div>
    </>
  );
}
